import { useFormState, useWatch, UseFormReturn } from 'react-hook-form';
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
    MdAdd,
    MdContentCopy,
    MdErrorOutline,
    MdMediation,
    MdOutlineContentCopy,
    MdOutlineDelete,
} from 'react-icons/md';
import Button from '@srm/ui/button';
import TableRowCount from '@srm/ui/table-row-count';
import TableEmptyState from '@srm/ui/table-empty-state';
import { useDocumentTableCount } from '@/context/DocumentTableCountContext';
import { resolveTableEmptyState } from './tableEmptyState';
import Tooltip from '@srm/ui/tooltip';
import ToolTipTitle from '@atoms/ToolTipTitle/ToolTipTitle';
import DictpickerModal from '@atoms/Dictpicker/DictpickerModal/DictpickerModal';
import { IDictionaryData } from '@models/dictionary/IDictionaryData';
import { checkGridRowsByScript } from '@utils/dictionaryFilters';
import { IField, IFieldElem } from '@models/IFormData';
import { IDocumentTable, ISetValue, ITableColumn, ITableColumnDict, TableColumn } from '@models/Forms/IForms';
import { applyEditFormFieldsToRowData } from '@utils/tableEditFormFields';
import { parseExternalDataSourceArrayKey } from '@utils/externalDataSourceUtils';
import Menu from '@srm/ui/menu';
import { TableToolbarButton } from '@srm/ui/table-actions';
import TableForm, { type TableFormDeleteActionState } from '@organisms/TableData/ModalForms/TableForm';
import SignCheckContainer from '@organisms/TableData/ModalForms/SignCheckContainer';
import TableData from '@organisms/TableData';
import { IDataTableColumn, IDataTableRevealRowRequest } from '@srm/ui/data-table';
import { useLatest } from '@srm/ui/hooks/useLatest';
import { sameKeys } from '@srm/ui/lib/sameKeys';
import { useDebugPanel } from '@/context/DebugPanelContext';
import { useActivated, useFormulaDisplay } from '@molecules/formbuilder/hooks';
import { AnalyticsContext } from '@/analytics/context/AnalyticsContext';
import { CentralizedSideEffectsContext, DocumentValidationIndexContext } from '@/context/Contexts';
import { validateDocumentTable } from '@utils/validation/document/tableValidation';
import { FormulaManager } from '@utils/managers/FormulaManager';
import { overrideTableColumns } from '@utils/debug/overrideColumn';
import { ValueType } from '@srm/ui/types/ValueType';
import {
    buildTableColumns,
    collectTableColumns,
    deriveTableWithColumns,
    formatGroupCellText,
    ITableColumnSource,
    TableColumnKind,
    TableRow,
} from './tableColumns';
import { createTableFormBridge } from './tableFormBridge';
import { formDataRowSetActionId, formDataRowSets } from './formDataRowSets';
import { TABLE_ADD_MENU_CLASS, tableAddChoiceIcon, tableAddMenuItems } from './tableAddMenu';
import { useExtendsDataSource } from './useExtendsDataSource';
import { useStableRows } from './useStableRows';
import ActionButton from '@molecules/RowActionButton/ActionButton';
import { LinkedDocsActionsService } from '@services/actions/LinkedDocsActionsService';
import { DocumentRuntimeContext, isDocumentRuntimeReadOnly } from '@/context/DocumentRuntimeContext';
import { useDocumentFormulaTraceRegistry } from '@/context/DocumentFormulaTraceContext';
import { AbookCellEditor, DictCellEditor, hasCustomTableEditor, renderTableEditor } from './tableEditors';
import { buildTableCellRender, cellTextOf, getCellState } from './tableCells';
import { collectHiddenColumnIssues } from './tableHiddenIssues';
import ConfirmPopup from './ConfirmPopup';
import OpenDocButton from './OpenDocButton';
import MergeRowsModal from './MergeRowsModal';
import SplitRowsModal from './SplitRowsModal';
import { RowFormOperationsContext } from '@organisms/TableData/ModalForms/RowFormOperationsContext';
import { buildSplitRows } from './tableMergeSplit';
import { useChangeHandlersOptional } from '@controls/ChangeHandlers/useChangeHandlers';
import type { IChangeFieldBinding, ChangeHandler } from '@utils/managers/ChangeRuntime';
import { reportChangeError } from '@controls/ChangeHandlers/changeErrors';

export interface IEditTableProps<TFieldValues extends object = object> {
    table: IDocumentTable;
    formMethods: UseFormReturn<TFieldValues>;
    fields: Record<string, IFieldElem>;
    docId?: string;
    isEdit: boolean;
    isNew: boolean;
    setError?: (messages?: string[]) => void;
    /** Паритет EditTable: гейт централизованного каскада (Table передаёт
     *  becameVisible+initComplete; без пропа — флаг из контекста). */
    skipCentralizedCascade?: boolean;
    turnOffInvokeChangesOnNew?: boolean;
    /** Режим подтаблицы (master-detail): путь массива данных в форме
     *  (`fields.[N].value.<idx>.<subKey>`) вместо fields[table.key]. */
    dataPath?: string;
    /** Родительская строка для формул с |Parent (режим подтаблицы). */
    rowParent?: TableRow;
    /** Изменение подтаблицы: родитель пересчитывает свою строку (calc-колонки). */
    onSubChanged?: () => Promise<void>;
    /** Режим просмотра: без редактирования, операций и выбора
     *  (view-ветка обычных таблиц; спец-ветки ViewTable остаются на легаси). */
    viewMode?: boolean;
    /** Унаследованное view-представление; не меняет режим формул и валидации. */
    displayOnly?: boolean;
}

import {
    IColumnRulesAccess,
    rowKeyOf,
    useColumnRules,
    useColumnVisibilityRules,
    useDetailVisibility,
    useMultiEditColumns,
    useRowButtonRules,
    useTableButtonRules,
} from './tableRuleHooks';
import { CopyColumnButton, RowEditButton, RowMultiEditButton, RowViewButton } from './tableButtons';
import ColumnFormulaBug from './ColumnFormulaBug';
import { DocumentFieldNavigationContext } from '@/context/DocumentFieldNavigationContext';
import {
    isTableColumnAbook,
    isTableColumnAutoComplete,
    isTableColumnCalc,
    isTableColumnDict,
} from '@utils/tableColumnGuards';

/** Форма «Значение для колонки»: копия таблицы с единственной колонкой без
 *  правил/каскадов (каскады применит пайплайн коммита). */
const buildMultiEditForm = (table: IDocumentTable, column: TableColumn): IDocumentTable => {
    const stripped = { ...column, changes: { change: [] }, visibilityRules: '', readonly: '', required: '' };
    const form = deriveTableWithColumns(table, { customForm: '' });
    if (isTableColumnDict(column)) form.tableColumnDict = [stripped as never];
    else if (isTableColumnAbook(column)) form.tableColumnAbook = [stripped as never];
    else if (isTableColumnAutoComplete(column)) form.tableColumnAutoComplete = [stripped as never];
    else if (isTableColumnCalc(column)) form.tableColumnCalc = [stripped as never];
    else form.tableColumn = [stripped as never];
    return form;
};

/** Виды колонок, редактируемые в принципе (не по правилу readonly): url не
 *  открывает редактор. calc правится руками, пока его не закрыл readonly
 *  (паритет легаси renderColumnCalcGrid): ручной ввод живёт до пересчёта формулы. */
const EDITABLE_KINDS = ['field', 'exds', 'dict', 'abook', 'autocomplete', 'calc'];

/** Числа уходят в Excel значением, чтобы по ним считать. У справочника, сотрудника и ссылки в ячейке
 *  текст displayFormat, а не значение, — они выгружаются текстом cellText. */
const EXCEL_TEXT_KINDS: readonly TableColumnKind[] = ['dict', 'abook', 'url'];
const EXCEL_NUMBER_TYPES: readonly (ValueType | undefined)[] = [ValueType.Money, ValueType.Double, ValueType.Integer];

/** Ширина слота кнопки строки (.tdn-action-slot в TableData.scss): иконочная
 *  кнопка занимает 22px, остаток — зазор между соседними кнопками. */
const ROW_ACTION_SLOT = 26;

/** Виды кнопок строки и трек шеврона — то, чем заняты служебные колонки. Обзор
 *  решает, занимать ли их место: пока про строки ничего не известно, место
 *  держим (безопасный дефолт — «кнопка видима»). */
interface IRowOverview {
    edit: boolean;
    copy: boolean;
    del: boolean;
    expandable: boolean;
}

const OVERVIEW_DEFAULT: IRowOverview = { edit: true, copy: true, del: true, expandable: true };

const ResolvedTableCaption = <TFieldValues extends object>({
    table,
    fields,
    formMethods,
    isEdit,
    isNew,
}: {
    table: IDocumentTable;
    fields: Record<string, IFieldElem>;
    formMethods: UseFormReturn<TFieldValues>;
    isEdit: boolean;
    isNew: boolean;
}) => {
    const { activated } = useActivated();
    const display = useFormulaDisplay(table.captionFormula, fields, formMethods, isEdit, isNew, activated, {
        fallback: table.caption,
        evalDisplayFormat: table.evalCaptionFormula,
        evaluateOnEmpty: true,
    });
    const caption = display.value || table.caption;

    return caption ? <span title={caption}>{caption}</span> : null;
};

type TableDrawerMode = 'view' | 'edit';

interface ITableDrawerState {
    rowKey: unknown;
    mode: TableDrawerMode;
}

/** Снимок значений формы для отката сеанса панели. `null` — снять не удалось
 *  (значение с неклонируемым содержимым): откат тогда честно недоступен, но
 *  открытие панели из-за этого падать не должно. */
const cloneFormSnapshot = <T,>(value: T): T | null => {
    try {
        return structuredClone(value);
    } catch (error) {
        console.error('EditTable: не удалось снять снимок формы для отката сеанса', error);
        return null;
    }
};

/** Редактируемость колонки: url отсекается по kind,
 *  readonly-правило — константой или формулой по строке. Правила приходят
 *  геттером (ref): их пересчёт не должен пересобирать columns. */
const editableOf = (
    source: ITableColumnSource | undefined,
    allowUpdating: boolean,
    getRules: () => IColumnRulesAccess,
): IDataTableColumn<TableRow>['editable'] => {
    if (!allowUpdating || !source || source.column.displayMode === 'view') return false;
    if (!EDITABLE_KINDS.includes(source.kind)) return false;
    const rule = 'readonly' in source.column ? source.column.readonly : undefined;
    if (rule === 'true') return false;
    if (!rule || rule === 'false') return true;
    return (row: TableRow) => getRules().for(row, source.columnId)?.readonly === false;
};

/** Содержимое таблицы-линков не загрузилось: объясняем, почему таблица пуста и заперта. */
const LinksFailedNotice = () => (
    <span className="document-table-field__links-error">
        <MdErrorOutline size="16" />
        Связанные документы не загружены — таблица недоступна для изменений
    </span>
);

/**
 * Новая ветка редактируемой таблицы форм (флаг DebugPanel «Таблицы на TanStack»).
 * Один источник истины — react-hook-form: строки из useWatch, правка ячейки —
 * пайплайн changes → формулы → точечный setValue → каскад агрегатов → trigger
 * (см. TABLEDATA-NEXT.md). Валидация — чистая
 * validateDocumentTable в RHF-правиле checkValid, без кэша isValid.
 */
const EditTable = <TFieldValues extends object = object>({
    table,
    formMethods,
    fields: sourceFields,
    docId,
    isEdit,
    isNew,
    setError,
    skipCentralizedCascade,
    turnOffInvokeChangesOnNew,
    dataPath,
    rowParent,
    onSubChanged,
    viewMode,
    displayOnly: inheritedDisplayOnly = false,
}: IEditTableProps<TFieldValues>) => {
    const operations = useContext(RowFormOperationsContext);
    const fields = useMemo(() => {
        if (!dataPath || !rowParent) return sourceFields;
        const parentPath = dataPath.slice(0, -(table.key.length + 1));
        return {
            ...sourceFields,
            ...Object.fromEntries(
                Object.entries(rowParent).map(([key, value]) => [
                    `|Parent${key}`,
                    { name: `${parentPath}.${key}`, index: -1, value } as IFieldElem,
                ]),
            ),
        };
    }, [sourceFields, dataPath, rowParent, table.key]);
    const parentAliases = useMemo(
        () => Object.fromEntries(Object.entries(fields).filter(([key]) => key.startsWith('|Parent|'))),
        [fields],
    );
    const displayOnly = !!viewMode || inheritedDisplayOnly || table.displayMode === 'view';
    const dataField = fields[table.key];
    // Содержимое таблицы-линков ещё едет отдельным запросом (useDocumentLinksLoad):
    // заглушка помечена pending — грид показывает загрузку вместо «Нет данных»,
    // а операции добавления строк скрыты (правки по заглушке были бы перетёрты).
    const linksPending = !!dataField?.pending;
    // Запрос содержимого отказал — в значении осталась заглушка. Таблица только для чтения:
    // правка «пустой» таблицы ушла бы в dirty-дифф как снятие всех связей документа.
    const linksFailed = !!dataField?.loadFailed;
    // Содержимого таблицы в форме нет — ни редактировать, ни считать по нему каскады.
    const linksUnavailable = linksPending || linksFailed;
    // Внешний массив (`|ExternalDataSource|<источник>`): такие поля в форму не
    // попадают (index -1) — строки живут только в карте полей, куда их дописывает
    // загрузчик источника. Следить за формой в этом случае не за чем.
    const externalArray = !!parseExternalDataSourceArrayKey(table.key);
    // режим подтаблицы: данные живут по пути внутри строки родителя
    const name = (dataPath ?? dataField?.name) as never;
    const hasData = !!dataPath || !!dataField;
    const watched = useWatch({ control: formMethods.control, name, disabled: !hasData || externalArray });
    const formRows: TableRow[] = useMemo(() => {
        const value = externalArray ? dataField?.value : watched;
        return Array.isArray(value) ? (value as TableRow[]) : [];
    }, [watched, externalArray, dataField?.value]);
    const dataKnown =
        hasData &&
        !linksUnavailable &&
        (!externalArray || (dataField?.pending === false && Array.isArray(dataField.value)));
    const totalRows = dataKnown ? formRows.length : undefined;
    // Подтаблица принадлежит строке, а не заголовку внешнего блока документа.
    const countInBlock = useDocumentTableCount(dataPath ? undefined : table.rowCount, totalRows);
    // Витрина поверх строк формы: SQL-процедура конфигурации (extendsDataSource)
    // дополняет строки полями, которых нет в документе. Только просмотр — в
    // редактировании её значения затирали бы вводимые.
    const extendsDs = useExtendsDataSource({
        table,
        rows: formRows,
        fields,
        formMethods,
        enabled: !!viewMode && !dataPath,
    });
    // прежние объекты неизменившихся строк: кэш строк грида перерисовывает только правленые; живые строки формы —
    // по getValues (внешний массив в форме не живёт — его строки сверяются со снимком всегда)
    const rows = useStableRows(extendsDs.rows, externalArray ? undefined : () => formMethods.getValues(name));
    // pause/refresh витрины зовутся из рендера колонки — держим их в ref, чтобы
    // пересоздание хука не пересобирало колонки
    const extendsDsRef = useLatest(extendsDs);

    // Меню действий над документом строки доступно только там, где действия вообще
    // разрешены (в support-просмотре карточка read-only) и только в режиме просмотра —
    // паритет ViewTable, где эта колонка и жила.
    const documentRuntime = useContext(DocumentRuntimeContext);
    const linkAction =
        viewMode && !isDocumentRuntimeReadOnly(documentRuntime)
            ? table.tableColumnAction?.linkDocumentAction
            : undefined;
    const linkedDocsActions = useMemo(() => (linkAction ? new LinkedDocsActionsService() : undefined), [linkAction]);

    const { settings: debugSettings, fieldOverrides } = useDebugPanel();
    const validationIndex = useContext(DocumentValidationIndexContext);
    const preserveResolverValidationState = validationIndex !== null;
    const validationDependents = useMemo(
        () => validationIndex?.getDependents(String(name)) ?? [],
        [name, validationIndex],
    );

    const setErrorRef = useLatest(setError);
    const formulaTraceRegistry = useDocumentFormulaTraceRegistry();
    const changeHandlers = useChangeHandlersOptional();
    const changeScopeId = String(name);
    const bridge = useMemo(
        () =>
            createTableFormBridge({
                fields,
                formMethods,
                isEdit,
                isNew,
                setError: (messages) => setErrorRef.current?.(messages),
                // режим подтаблицы: |Parent|-поля формул резолвятся мостом
                rowParent,
                // инспектор формул DebugPanel: правила таблицы видны в трассах
                traceRegistry: formulaTraceRegistry,
                changeRuntime: changeHandlers?.runtime,
                invokeSavedChange: changeHandlers ? (binding, value, row, activeTargets, oldRow) =>
                    changeHandlers.runSavedChanges(binding, value, row, oldRow, { activeTargets: activeTargets ?? new Set(), scopeId: binding.scopeId }) : undefined,
                scopeId: changeScopeId,
            }),
        // setErrorRef — стабильный ref
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [fields, formMethods, isEdit, isNew, rowParent, formulaTraceRegistry, changeHandlers, changeScopeId],
    );

    // Обработчики — цели invokeChanges: вызываются только изнутри чужого каскада, поэтому ошибку
    // не ловят — каскад-источник обрывается, показывает её корень вызова.
    useEffect(() => {
        const runtime = changeHandlers?.runtime;
        if (!runtime) return;
        const registrations: Array<{ binding: IChangeFieldBinding; handler: ChangeHandler }> = [];
        if (!dataPath) {
            const binding = { key: table.key };
            const handler: ChangeHandler = async (request) => {
                await bridge.onTableChange(request.value, table, true, request.invocation);
            };
            runtime.register(binding, handler);
            registrations.push({ binding, handler });
        }
        const columnsByKey = new Map<string, TableColumn[]>();
        for (const { column } of collectTableColumns(table)) {
            if (!column.key || !('changes' in column)) continue;
            columnsByKey.set(column.key, [...(columnsByKey.get(column.key) ?? []), column]);
        }
        for (const [key, candidates] of columnsByKey) {
            const binding = { key, tableKey: table.key, scopeId: changeScopeId };
            const handler: ChangeHandler = async (request) => {
                const currentRow = request.rowData;
                if (!currentRow) return;
                for (const column of candidates) {
                    if (!(await bridge.evalRowRule(column.visibilityRules, currentRow))) continue;
                    await bridge.onChangeCellValue(currentRow, request.rowOldData ?? currentRow, column, table, false, request.invocation);
                    break;
                }
            };
            runtime.register(binding, handler);
            registrations.push({ binding, handler });
        }
        return () => registrations.forEach(({ binding, handler }) => runtime.unregister(binding, handler));
    }, [changeHandlers?.runtime, table, changeScopeId, bridge, dataPath]);
    const onSubChangedRef = useLatest(onSubChanged);

    // Паритет EditTable: в централизованном режиме стартовый каскад покрыт
    // runInvokeChangesInit; гейт (becameVisible+initComplete) передаёт Table,
    // fallback — флаг контекста. Подтаблицы (dataPath) каскад не запускают —
    // их changes гоняет родитель. Ref-гарды: один прогон на маунт.
    const centralizedSideEffects = useContext(CentralizedSideEffectsContext);
    const skipCascade = skipCentralizedCascade ?? centralizedSideEffects;
    // linksUnavailable в гарде и deps обоих каскадов: содержимое таблицы-линков едет отдельным
    // запросом, и одноразовый каскад по заглушке [] посчитал бы правила по пустой таблице
    // без пересчёта после прихода строк. Ref не взводится, пока строк нет — эффект перезапустится
    // по их приходе и прогонит каскад уже по реальным строкам; при отказе запроса не прогонит вовсе.
    const newCascadeRan = useRef(false);
    useEffect(() => {
        if (skipCascade || newCascadeRan.current || dataPath || linksUnavailable) return;
        if (isNew && table.invokeChangesOnNew && !turnOffInvokeChangesOnNew) {
            newCascadeRan.current = true;
            void bridge
                .onTableChange(readRows(), table)
                .catch((error) => console.error('EditTable: ошибка стартового каскада (new)', error));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skipCascade, isNew, turnOffInvokeChangesOnNew, linksUnavailable]);
    const editCascadeRan = useRef(false);
    useEffect(() => {
        if (skipCascade || editCascadeRan.current || dataPath || linksUnavailable) return;
        if (isEdit && table.invokeChangesOnEdit) {
            editCascadeRan.current = true;
            void bridge
                .onTableChange(readRows(), table)
                .catch((error) => console.error('EditTable: ошибка стартового каскада (edit)', error));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skipCascade, isEdit, linksUnavailable]);

    const allowUpdating =
        !table.updateRowsDisabled &&
        !displayOnly &&
        !linksUnavailable &&
        !isDocumentRuntimeReadOnly(documentRuntime) &&
        !(table.emptyState?.enabled && externalArray);
    // Схема колонок с дебаг-оверрайдами жучка (readonly/required/visibility/min/max/
    // pattern/maxLength). Паритет TableData: оверрайд обязан влиять и на грид, а не
    // только на валидацию — иначе правила расходятся с тем, что видит пользователь.
    // Без оверрайдов возвращается исходный объект (identity сохраняется).
    const schema = useMemo(() => overrideTableColumns(table, fieldOverrides), [table, fieldOverrides]);
    // жучок правил в шапке колонки — по флагу DebugPanel, как у полей формы
    const showFormulaBug = debugSettings.showFieldFormulas;
    const columnRules = useColumnRules(schema, bridge, formMethods);
    // правила читаются из ref внутри замыканий render/editable/renderEditor:
    // их пересчёт (на каждый чих строк) не пересобирает columns
    const columnRulesRef = useLatest(columnRules);
    const columnById = useMemo(() => {
        const map = new Map<string, ITableColumnSource>();
        collectTableColumns(schema).forEach((source) => map.set(source.columnId, source));
        return map;
    }, [schema]);

    /** Текущий массив строк из формы (единый источник). */
    const readRows = useCallback((): TableRow[] => {
        const value = formMethods.getValues(name);
        return Array.isArray(value) ? value : [];
    }, [formMethods, name]);

    // Один drawer принадлежит таблице, а не кнопке конкретной строки: строка
    // может быть удалена/добавлена, не размонтируя панель и её сеанс отмены.
    const [drawerState, setDrawerState] = useState<ITableDrawerState | null>(null);
    const [revealRowRequest, setRevealRowRequest] = useState<IDataTableRevealRowRequest | null>(null);
    // Строку, которую редактируют в drawer, показываем после закрытия панели:
    // иначе короткая подсветка закончится за непрозрачным оверлеем.
    const pendingRevealRowRef = useRef<string | null>(null);
    const revealRequestTokenRef = useRef(0);
    const requestRowReveal = useCallback((rowId: unknown, focus = true) => {
        revealRequestTokenRef.current += 1;
        setRevealRowRequest({ rowId: String(rowId), token: revealRequestTokenRef.current, focus });
    }, []);
    const [drawerSessionChangeCount, setDrawerSessionChangeCount] = useState(0);
    const drawerSessionSnapshotRef = useRef<TFieldValues | null>(null);
    /** Снимок сеанса уже снят (в т.ч. неудачно) — второй раз не снимаем. */
    const drawerSessionArmedRef = useRef(false);
    const [drawerDeleteUndo, setDrawerDeleteUndo] = useState<{
        snapshot: TFieldValues;
        rowKey: unknown;
    } | null>(null);

    const captureFormSnapshot = useCallback(() => cloneFormSnapshot(formMethods.getValues()), [formMethods]);
    /**
     * Возврат ВСЕЙ формы к снимку. Именно всей, а не одной таблицы: `changes`
     * строки пишет и поля документа, и соседние таблицы — откатить строки, оставив
     * их последствия, значит получить рассогласованный черновик.
     *
     * Обратная сторона: данные, приехавшие в форму асинхронно уже ПОСЛЕ снимка
     * (содержимое таблиц-линков, внешние источники), откатом тоже снесёт до
     * заглушки. Поэтому снимок снимается как можно позже — перед первой
     * операцией сеанса, а не на открытии панели.
     */
    const restoreFormSnapshot = useCallback(
        async (snapshot: TFieldValues) => {
            const wasSubmitted = formMethods.formState.isSubmitted;
            formMethods.reset(cloneFormSnapshot(snapshot) ?? snapshot, {
                keepDefaultValues: true,
                keepTouched: true,
                // Без keepFieldsRef reset обнуляет карту зарегистрированных полей
                // формы, а вместе с ней — императивные `register(name, {validate})`:
                // правило checkValid каждой таблицы и блока вложений регистрируется
                // эффектом на маунте и после сброса уже не восстанавливается, то есть
                // документ становится сохраняемым с невалидной таблицей.
                keepFieldsRef: true,
                // Откат строк — не «форму не отправляли»: счётчик отправок и признак
                // submit принадлежат карточке, а не сеансу панели.
                keepIsSubmitted: true,
                keepSubmitCount: true,
            });
            // `changes` строки может менять не только эту таблицу, но и поля
            // документа/соседние таблицы. Откат редкий, поэтому безопаснее
            // сбросить весь fact-кэш, чем оставить факты от отменённого сеанса.
            FormulaManager.InvalidateFacts();
            // reset уже очистил ошибки. Пересчитывать их по всей форме можно только
            // там, где они и так на экране: иначе отмена сеанса зажигала бы разом
            // все незаполненные обязательные поля карточки, которую не отправляли.
            if (wasSubmitted) await formMethods.trigger();
        },
        [formMethods],
    );
    /** Снимок для отката сеанса снимается перед ПЕРВОЙ операцией панели: просмотр
     *  строки и навигация по строкам не должны платить за клон всей формы — на
     *  таблице в тысячи строк это заметная пауза прямо на клике. */
    const ensureDrawerSessionSnapshot = useCallback(() => {
        if (drawerSessionArmedRef.current) return;
        drawerSessionArmedRef.current = true;
        drawerSessionSnapshotRef.current = captureFormSnapshot();
    }, [captureFormSnapshot]);
    const openDrawer = useCallback((rowKey: unknown, mode: TableDrawerMode, snapshot?: TFieldValues | null) => {
        // snapshot приходит только от «добавить строку с открытием формы»: там
        // строка уже создана, и снимать сеанс поздно — он снят до создания.
        drawerSessionArmedRef.current = snapshot !== undefined;
        drawerSessionSnapshotRef.current = snapshot ?? null;
        setDrawerSessionChangeCount(0);
        setDrawerDeleteUndo(null);
        setDrawerState({ rowKey, mode });
    }, []);
    const closeDrawer = useCallback(() => {
        const pendingRow = pendingRevealRowRef.current;
        pendingRevealRowRef.current = null;
        drawerSessionSnapshotRef.current = null;
        drawerSessionArmedRef.current = false;
        setDrawerSessionChangeCount(0);
        setDrawerDeleteUndo(null);
        setDrawerState(null);
        if (pendingRow) requestRowReveal(pendingRow);
    }, [requestRowReveal]);
    const cancelDrawerSession = useCallback(async () => {
        if (!drawerSessionArmedRef.current) return;
        const snapshot = drawerSessionSnapshotRef.current;
        // Молча «отменить» и ничего не откатить нельзя — панель покажет отказ.
        if (!snapshot) throw new Error('Не удалось вернуть таблицу к состоянию до открытия панели');
        await restoreFormSnapshot(snapshot);
        pendingRevealRowRef.current = null;
    }, [restoreFormSnapshot]);
    const markDrawerChange = useCallback(() => {
        setDrawerSessionChangeCount((count) => count + 1);
        setDrawerDeleteUndo(null);
    }, []);

    /** Обёртка операций: fire-and-forget с логом ошибки (единый шаблон колбэков). Вне формы
     *  строки ошибка (например, callAction в changes ячейки) ещё и показывается: там её больше
     *  никто не увидит; в форме строки отказ операции показывает TableForm при применении. */
    const runOp = useCallback(
        (label: string, op: () => Promise<void>) => {
            void (operations ? operations.run(op) : op()).catch((error: unknown) => {
                console.error(`EditTable: ${label}`, error);
                if (!operations) reportChangeError(error, setErrorRef.current, 'Не удалось выполнить операцию над таблицей.');
            });
        },
        [operations, setErrorRef],
    );

    /**
     * Пересчёт ошибки таблицы после операции. Это полный обход всех строк
     * (validateDocumentTable), поэтому он имеет смысл только там, где ошибку
     * действительно кто-то показывает: она уже висит на таблице (правка должна
     * её снять) или карточку отправляли и ошибки на экране. До первой отправки
     * валидной таблице пересчёт ничего не меняет, а на таблице в полторы тысячи
     * строк стоит больше секунды на каждую применённую строку.
     *
     * Подсветка невалидных ячеек считается отдельно, по самой строке
     * (tableCells.getCellState), и от этого пересчёта не зависит.
     */
    const revalidateTable = useCallback(async () => {
        if (!formMethods.formState.isSubmitted && !formMethods.getFieldState(name).error) return;
        await formMethods.trigger(name);
    }, [formMethods, name]);

    // ----- Пайплайн коммита: патч строки (одно поле или набор от справочника) -----
    const commitRowPatch = useCallback(
        (row: TableRow, source: ITableColumnSource, patch: Record<string, unknown>) => {
            if (!hasData || !allowUpdating || source.column.displayMode === 'view') return;
            runOp('ошибка коммита ячейки', async () => {
                const list = readRows();
                const idx = list.findIndex((r) => r['|NUM'] === row['|NUM']);
                if (idx < 0) return;
                const oldRow = { ...list[idx] };
                const next: TableRow = { ...list[idx], ...patch };
                // changes и пересчёт зависимых колонок атомарны: строку в форму пишем
                // одним setValue ниже. Промежуточная публикация (withOutUpdate=false)
                // будила подписчиков на недосчитанной строке — EUPDEV-12836.
                await bridge.onChangeCellValue(next, oldRow, source.column, table, true);
                await bridge.calculateRow(next, source.column, table);
                // changes могли изменить состав таблицы; старый индекс больше не надёжен.
                const currentIndex = readRows().findIndex((item) => item['|NUM'] === next['|NUM']);
                if (currentIndex < 0) return;
                formMethods.setValue(`${String(name)}.${currentIndex}` as never, next as never, { shouldDirty: true });
                await bridge.flushPendingRowChanges(next);
                await bridge.onTableChange(formMethods.getValues(name), table);
                await onSubChangedRef.current?.();
                await revalidateTable();
            });
        },
        // prettier-ignore
        [bridge, hasData, allowUpdating, formMethods, name, table, readRows, runOp, onSubChangedRef, revalidateTable],
    );

    const onCellCommit = useCallback(
        (row: TableRow, field: string, value: unknown) => {
            // field = columnId грида; патч пишем в data-ключ конфигурации
            const source = columnById.get(field);
            if (source) commitRowPatch(row, source, { [source.column.key]: value });
        },
        [columnById, commitRowPatch],
    );

    const columnVisibility = useColumnVisibilityRules(schema, bridge, formMethods);

    // ----- Выбор строк (мультиудаление/мультиредактирование) -----
    const multiEditColumns = useMultiEditColumns(schema, bridge, formMethods);
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
    // Грид сообщает выбор и на маунте — пустым массивом. Новая ссылка на тот же выбор
    // перерисовывала бы всю таблицу ещё раз сразу после первой отрисовки.
    const onSelectionChange = useCallback((keys: string[]) => {
        setSelectedKeys((prev) => (sameKeys(prev, keys) ? prev : keys));
    }, []);
    const selectedKeysRef = useLatest(selectedKeys);
    // булево, а не массив: колонки пересобираются только на переходе «есть выбор/нет»
    const hasSelection = selectedKeys.length > 0;
    // карандаш мультиправки в шапке: без таких колонок выбор колонки (и все ячейки) не пересобирает
    const multiEditShown = hasSelection && multiEditColumns.size > 0;
    const rowsRef = useLatest(rows);
    const getSelectedRows = useCallback(() => {
        const keys = new Set(selectedKeysRef.current);
        return rowsRef.current.filter((row) => keys.has(rowKeyOf(row)));
    }, [rowsRef, selectedKeysRef]);
    const getAllRows = useCallback(() => rowsRef.current, [rowsRef]);
    const getColumnRules = useCallback(() => columnRulesRef.current, [columnRulesRef]);
    // Правила считаются по мере показа строк, а выбрать можно и те, что сейчас за
    // краем окна прокрутки («выбрать все»). Мультиредактирование смотрит на их
    // readonly в момент клика — прогреваем выбор заранее, чтобы проверка не
    // приняла ещё не посчитанную строку за редактируемую.
    useEffect(() => {
        if (!hasSelection) return;
        columnRules.request(getSelectedRows());
    }, [hasSelection, selectedKeys, columnRules, getSelectedRows]);
    // модалка «Значение для колонки» живёт здесь, а не в кнопке шапки: шапка
    // грида ремаунтится и локальный state открытости в ней теряется
    const [multiEditState, setMultiEditState] = useState<{
        source: ITableColumnSource;
        row: TableRow;
    } | null>(null);
    const onMultiEditOpen = useCallback(
        (source: ITableColumnSource, firstSelectedRow: TableRow) =>
            setMultiEditState({ source, row: firstSelectedRow }),
        [],
    );

    const cellDeps = useMemo(
        () => ({
            rulesFor: (row: TableRow, columnId: string) => columnRulesRef.current.for(row, columnId),
            onInstantCommit: (row: TableRow, columnId: string, value: unknown) => {
                if (!allowUpdating) return;
                const src = columnById.get(columnId);
                if (src) commitRowPatch(row, src, { [src.column.key]: value });
            },
            skipRequired: debugSettings.skipAllValidation || debugSettings.skipRequiredValidation,
            skipPattern: debugSettings.skipAllValidation || debugSettings.skipPatternValidation,
            skipLength: debugSettings.skipAllValidation || debugSettings.skipLengthValidation,
            // просмотр: без подсветки invalid/readonly и мгновенных чекбоксов
            displayOnly,
            readOnly:
                !!table.updateRowsDisabled ||
                isDocumentRuntimeReadOnly(documentRuntime) ||
                !!(table.emptyState?.enabled && externalArray),
        }),
        // prettier-ignore
        [columnRulesRef, columnById, commitRowPatch, debugSettings.skipAllValidation, debugSettings.skipRequiredValidation, debugSettings.skipPatternValidation, debugSettings.skipLengthValidation, displayOnly, allowUpdating, table.updateRowsDisabled, documentRuntime, table.emptyState?.enabled, externalArray],
    );

    const columns = useMemo(() => {
        const mapped = buildTableColumns(schema).map((column) => {
            const source = columnById.get(column.field);
            const custom =
                source && column.dataType !== undefined && hasCustomTableEditor(source.kind, column.dataType);
            // хинты заголовка в модели раздельные: title — редактирование, viewTitle — просмотр
            const columnTitle = source
                ? displayOnly || source.column.displayMode === 'view'
                    ? 'viewTitle' in source.column
                        ? (source.column as ITableColumn).viewTitle
                        : undefined
                    : 'title' in source.column
                      ? (source.column as ITableColumn).title
                      : undefined
                : undefined;
            // карандаш мультиредактирования — только при выбранных строках (как
            // корзина мультиудаления в шапке колонки действий); ширину текста он
            // не трогает — headerExtra живёт в наплыве инструментов шапки атома
            const showMultiEdit = !!source && source.column.displayMode !== 'view' && allowUpdating && multiEditShown && multiEditColumns.has(source.columnId);
            const showCopyColumn = !!source && !!source.column.showCopyColumnContentButton;
            const headerExtra =
                source && (showMultiEdit || showCopyColumn || showFormulaBug) ? (
                    <>
                        {showMultiEdit && (
                            <RowMultiEditButton
                                source={source}
                                getColumnRules={getColumnRules}
                                getSelectedRows={getSelectedRows}
                                onOpen={onMultiEditOpen}
                            />
                        )}
                        {showCopyColumn && <CopyColumnButton column={source.column} getRows={getAllRows} />}
                        {/* жучок правил колонки (DebugPanel): у колонки нет своего поля формы,
                            поэтому только формулы правил и их оверрайды — без редактора значения */}
                        {showFormulaBug && <ColumnFormulaBug column={source.column} viewMode={!!viewMode} />}
                    </>
                ) : undefined;
            const groupCaption = column.header ?? '';
            return {
                ...column,
                headerExtra,
                // справка колонки видна всегда (касание, Tab), а не только под курсором, как инструменты шапки
                headerHint: columnTitle ? <ToolTipTitle title={columnTitle} label={column.header} /> : undefined,
                // header-фильтр и выгрузка показывают то же, что видно в ячейке
                cellText: source ? (row: TableRow) => cellTextOf(source.column, row) : undefined,
                ...(source &&
                    !EXCEL_TEXT_KINDS.includes(source.kind) &&
                    EXCEL_NUMBER_TYPES.includes(column.dataType) && { excelFormat: true as const }),
                groupRender: source
                    ? ({ value, rows: groupRows }: { value: unknown; rows: TableRow[] }) =>
                          `${formatGroupCellText(source.column, groupCaption, String(value ?? ''))} (${groupRows.length})`
                    : undefined,
                groupExportText: source
                    ? ({ value, rows: groupRows }: { value: unknown; rows: TableRow[] }) =>
                          `${formatGroupCellText(source.column, groupCaption, String(value ?? ''))} (${groupRows.length})`
                    : undefined,
                visible: column.visible !== false && (columnVisibility.get(column.field) ?? true),
                render: source
                    ? buildTableCellRender(
                          source.column,
                          source.columnId,
                          cellDeps,
                          EDITABLE_KINDS.includes(source.kind),
                      )
                    : undefined,
                editable:
                    source && 'valueType' in source.column && source.column.valueType === ValueType.Boolean
                        ? false // Boolean правится мгновенным чекбоксом в ячейке
                        : editableOf(source, allowUpdating, getColumnRules),
                ...(custom && source
                    ? {
                          renderEditor: (
                              ctx: Parameters<NonNullable<IDataTableColumn<TableRow>['renderEditor']>>[0],
                          ) => {
                              const commitPatch = (patch: Record<string, unknown>) => {
                                  ctx.cancel();
                                  commitRowPatch(ctx.row, source, patch);
                              };
                              const writePatch = (patch: Record<string, unknown>) =>
                                  commitRowPatch(ctx.row, source, patch);
                              if (source.kind === 'dict') {
                                  return (
                                      <DictCellEditor
                                          column={source.column as ITableColumnDict}
                                          row={ctx.row}
                                          docId={docId}
                                          bridge={bridge}
                                          onCommitPatch={commitPatch}
                                          onWritePatch={writePatch}
                                          onCancel={ctx.cancel}
                                      />
                                  );
                              }
                              if (source.kind === 'abook') {
                                  return (
                                      <AbookCellEditor
                                          column={source.column as never}
                                          row={ctx.row}
                                          docId={docId}
                                          bridge={bridge}
                                          onCommitPatch={commitPatch}
                                          onWritePatch={writePatch}
                                          onCancel={ctx.cancel}
                                      />
                                  );
                              }
                              return renderTableEditor(
                                  source.kind,
                                  source.column,
                                  ctx,
                                  columnRulesRef.current.for(ctx.row, source.columnId) ?? {},
                              );
                          },
                      }
                    : {}),
            };
        });
        const virtual: IDataTableColumn<TableRow>[] = [];
        // виртуальная колонка «просмотреть документ строки» (previewDocByKey)
        if (table.previewDocByKey) {
            virtual.push({
                field: '|__openDoc',
                header: table.previewDocTitle || 'Просмотреть',
                hideHeaderText: true,
                width: 36,
                pinned: 'right',
                allowSorting: false,
                allowFiltering: false,
                allowHeaderFiltering: false,
                allowResizing: false,
                allowReordering: false,
                allowHiding: false,
                render: (row: TableRow) => <OpenDocButton table={table} row={row} docId={docId} />,
            });
        }
        // меню действий над документом строки (tableColumnAction.linkDocumentAction):
        // строки такой таблицы — ссылки на другие документы, действия берутся у них
        if (linkAction) {
            virtual.push({
                field: '|__linkActions',
                header: 'Действия',
                hideHeaderText: true,
                width: 44,
                pinned: 'right',
                allowSorting: false,
                allowFiltering: false,
                allowHeaderFiltering: false,
                allowResizing: false,
                allowReordering: false,
                allowHiding: false,
                render: (row: TableRow) => (
                    <ActionButton
                        objId={`${docId ?? ''}/${String(row[linkAction.linkKey] ?? '')}`}
                        objData={row}
                        service={linkedDocsActions}
                        // меню открыто — автообновление витрины не должно его закрывать. Ячейка берётся из
                        // кэша строк грида: витрину зовём свежую, а не ту, что была при её рисовании
                        onActionClick={() => extendsDsRef.current.pause()}
                        onModifyData={() => extendsDsRef.current.refresh()}
                    />
                ),
            });
        }
        return virtual.length ? [...mapped, ...virtual] : mapped;
        // prettier-ignore
    }, [
        table,
        schema,
        columnById,
        allowUpdating,
        columnRulesRef,
        getColumnRules,
        bridge,
        commitRowPatch,
        docId,
        columnVisibility,
        cellDeps,
        multiEditColumns,
        multiEditShown,
        getSelectedRows,
        onMultiEditOpen,
        getAllRows,
        displayOnly,
        viewMode,
        showFormulaBug,
        linkAction,
        linkedDocsActions,
        extendsDsRef,
    ]);

    // ----- Валидация: чистая функция от значения формы (без кэша isValid) -----
    const debugSettingsRef = useLatest(debugSettings);

    const checkValid = useCallback(async () => {
        if (debugSettingsRef.current.skipAllValidation) return true;
        const value = formMethods.getValues(name);
        if (value == null || (Array.isArray(value) && value.length === 0)) return true;
        try {
            const result = await validateDocumentTable(
                schema,
                Array.isArray(value) ? value : [],
                // валидация идёт по ВСЕМ строкам, а правила required/видимости у них
                // одни и те же — вычислитель с переиспользованием результата снимает
                // с таблицы в тысячи строк основную часть работы
                (rule, row, parentRow) => bridge.evalTableRuleValue(rule, row as TableRow, parentRow as TableRow),
                debugSettingsRef.current,
            );
            return result.valid;
        } catch (error) {
            // fail-open: сбой пересчёта не должен навечно блокировать сохранение
            console.error(error);
            return true;
        }
    }, [bridge, debugSettingsRef, formMethods, name, schema]);
    const checkValidRef = useLatest(checkValid);

    // ----- Сигнал «ошибки в столбцах, спрятанных набором колонок» -----
    // Валидация набор колонок не учитывает: незаполненное обязательное поле в
    // скрытом столбце блокирует сохранение, а на экране красных ячеек нет.
    const getHiddenIssues = useCallback(
        async (hiddenFields: string[]) => {
            if (viewMode || debugSettingsRef.current.skipAllValidation) return null;
            const hidden = new Set(hiddenFields);
            const sources = collectTableColumns(schema).filter(
                // колонку, скрытую формулой видимости, не проверяет и валидация
                (source) => hidden.has(source.columnId) && columnVisibility.get(source.columnId) !== false,
            );
            if (sources.length === 0) return null;
            const value = formMethods.getValues(name);
            return collectHiddenColumnIssues(
                sources,
                Array.isArray(value) ? (value as TableRow[]) : [],
                (rule, row) => bridge.evalTableRuleValue(rule, row) as Promise<unknown>,
                cellDeps,
            );
        },
        [viewMode, debugSettingsRef, schema, columnVisibility, formMethods, name, bridge, cellDeps],
    );

    // Отказ проверки формы эскалирует сигнал и возвращает его, даже если
    // подсказку закрыли: иначе сохранение молча не проходит второй раз подряд.
    const [validationFailedEpoch, setValidationFailedEpoch] = useState(0);
    // подписка именно на ошибку таблицы: `errors` RHF мутирует на месте, и без
    // useFormState ссылка осталась бы прежней
    const tableFormState = useFormState({ control: formMethods.control, name });
    const tableError = !!formMethods.getFieldState(name, tableFormState).error;
    useEffect(() => {
        if (tableError) setValidationFailedEpoch((epoch) => epoch + 1);
    }, [tableError]);

    useEffect(() => {
        // подтаблицу валидирует родитель (validateDocumentTable обходит tables);
        // в режиме просмотра валидация не нужна
        if (!dataField || dataPath || viewMode) return;
        formMethods.register(name, {
            // через ref: пересборка bridge/checkValid не должна давать unregister/register —
            // каждая пара эмитит состояние формы, и форма строки уходила в цикл
            validate: { checkValid: () => checkValidRef.current() },
            ...(validationDependents.length > 0 ? { deps: [...validationDependents] as never } : {}),
        });
        return () => {
            if (!preserveResolverValidationState) {
                formMethods.clearErrors(name);
            }
            formMethods.unregister(name, {
                keepValue: true,
                keepDirty: true,
                keepTouched: true,
                keepDefaultValue: true,
                ...(preserveResolverValidationState ? { keepError: true, keepIsValid: true } : {}),
            });
        };
    }, [
        formMethods,
        name,
        dataField,
        dataPath,
        viewMode,
        checkValidRef,
        validationDependents,
        preserveResolverValidationState,
    ]);

    // ----- Операции строк: массив формы → один setValue → каскад агрегатов -----
    const buttonRules = useTableButtonRules(table, bridge, formMethods);
    const formDataSets = useMemo(() => formDataRowSets(table), [table]);
    // наборы добавления из FormData, доступные сейчас: условие набора и правило его кнопки истинны
    const pickableSets = useMemo(
        () => (allowUpdating ? formDataSets.filter((set) => buttonRules.formDataSets.includes(set.key)) : []),
        [allowUpdating, formDataSets, buttonRules.formDataSets],
    );
    const formDataOptions = useMemo(
        () => pickableSets.map((set) => ({ key: set.key, label: set.config.name })),
        [pickableSets],
    );
    const addCapabilitiesRef = useLatest({
        canAdd: allowUpdating && buttonRules.canAdd,
        pickable: pickableSets.map((set) => set.key),
    });
    const rowButtonRules = useRowButtonRules(table, bridge, formMethods);
    // Обзор состава служебных колонок считается по строкам, которые таблица
    // ДЕЙСТВИТЕЛЬНО отрисовала: только их правила посчитаны (кэш строчных правил
    // наполняют сами строки), и только их содержимое актуально — строки приходят
    // из useWatch новыми клонами, а сверять весь набор на каждую нотификацию
    // формы значило бы платить за это на каждой правке ячейки.
    const [overview, setOverview] = useState<IRowOverview>(OVERVIEW_DEFAULT);
    const renderedRowsRef = useRef<TableRow[]>([]);
    // Кэш меняет identity на каждой досчитанной порции правил (version) — это
    // нужно только обзору состава командной колонки. Рисующие строку функции
    // читают его через ref, иначе они пересобирались бы всю дорогу расчёта.
    const rowButtonRulesRef = useLatest(rowButtonRules);
    // Выбрать можно и строку за краем окна прокрутки, а по правилам выбранных
    // строк решается, показывать ли корзину массового удаления, — прогреваем.
    useEffect(() => {
        if (!hasSelection) return;
        rowButtonRules.request(getSelectedRows());
    }, [hasSelection, selectedKeys, rowButtonRules, getSelectedRows]);

    const applyRows = useCallback(
        async (next: TableRow[]) => {
            formMethods.setValue(name, next as never, { shouldDirty: true });
            for (const row of next) await bridge.flushPendingRowChanges(row);
            await bridge.onTableChange(formMethods.getValues(name), table);
            await onSubChangedRef.current?.();
            await revalidateTable();
        },
        [bridge, formMethods, name, onSubChangedRef, revalidateTable, table],
    );

    /** Вставка новой строки с применением set-правил и общим дедупом по |NUM —
     *  страховка от повторной вставки той же строки (исторически прикрывала
     *  мутацию живого массива RHF в GetFormulaValues; мутация устранена —
     *  формулы с self-reference считают по копии). */
    const insertRow = useCallback(
        async (item: TableRow, sets: ISetValue[] | undefined): Promise<TableRow[]> => {
            await bridge.applyRowSets(sets, table, item);
            const arr = readRows();
            const next = arr.some((r) => r['|NUM'] === item['|NUM']) ? [...arr] : [...arr, item];
            await applyRows(next);
            return next;
        },
        [applyRows, bridge, readRows, table],
    );

    /** @param focusCell навести курсор в ячейку `addRowFocusColumn` прямо в гриде.
     *  Строку, которую сразу открывает панель, фокусировать в гриде нельзя:
     *  редактор ячейки открылся бы под её оверлеем и остался бы висеть после
     *  закрытия панели. */
    const createRow = useCallback(
        async (focusCell = false): Promise<TableRow> => {
            const item: TableRow = { '|NUM': uuidv4() };
            await insertRow(item, table.newRowData?.setValues?.sets);
            if (focusCell && table.addRowFocusColumn) {
                const focusSource = collectTableColumns(table).find(
                    (source) => source.column.key === table.addRowFocusColumn,
                );
                if (focusSource) {
                    setEditCellRequest({
                        rowId: String(item['|NUM']),
                        field: focusSource.columnId,
                        token: Date.now(),
                    });
                }
            }
            return item;
        },
        [insertRow, table],
    );

    const addRow = useCallback(() => {
        if (!addCapabilitiesRef.current.canAdd) return;
        const snapshot = table.showEditFormOnAddRow ? captureFormSnapshot() : undefined;
        runOp('ошибка добавления строки', async () => {
            const item = await createRow(!table.showEditFormOnAddRow);
            if (table.showEditFormOnAddRow) {
                pendingRevealRowRef.current = String(item['|NUM']);
                openDrawer(item['|NUM'], 'edit', snapshot);
                setDrawerSessionChangeCount(1);
            } else {
                requestRowReveal(item['|NUM'], !table.addRowFocusColumn);
            }
        });
    }, [addCapabilitiesRef, captureFormSnapshot, createRow, openDrawer, requestRowReveal, runOp, table]);

    const removeRow = useCallback(
        async (row: TableRow) => {
            const next = readRows().filter((r) => r['|NUM'] !== row['|NUM']);
            await applyRows(next);
            await bridge.onTableRowDeleted(next, row, table);
            return next;
        },
        [applyRows, bridge, readRows, table],
    );

    const deleteRow = useCallback(
        (row: TableRow) => {
            runOp('ошибка удаления строки', async () => void (await removeRow(row)));
        },
        [removeRow, runOp],
    );

    const copyRow = useCallback(
        (row: TableRow) => {
            runOp('ошибка копирования строки', async () => {
                const item: TableRow = { ...row, '|NUM': uuidv4() };
                // у строк таблиц-линков копия не наследует привязку к документу
                delete item['|doc_Id'];
                const next = await insertRow(item, table.copyRowData?.setValues?.sets);
                await bridge.onTableRowCopied(next, item, table);
                requestRowReveal(item['|NUM']);
            });
        },
        [bridge, insertRow, requestRowReveal, runOp, table],
    );

    // ----- Мультиредактирование: значение колонки во все выбранные строки -----
    const saveEditRowMulti = useCallback(
        (data: IField[], source: ITableColumnSource) => {
            if (!allowUpdating || source.column.displayMode === 'view') return;
            runOp('ошибка мультиредактирования', async () => {
                const arr = [...readRows()];
                const selected = new Set(selectedKeysRef.current);
                let changed = false;
                for (let idx = 0; idx < arr.length; idx++) {
                    if (!selected.has(rowKeyOf(arr[idx]))) continue;
                    const orig = arr[idx];
                    let next: TableRow = { ...orig };
                    data.forEach((field) => {
                        // поля таблиц-линков и документа не пишутся в строку
                        if (field.name.startsWith('#links|')) return;
                        if (field.name.indexOf('|Document') === -1) next[field.name] = field.value;
                    });
                    await bridge.onChangeCellValue(next, orig, source.column, table, true);
                    next = await bridge.calculateRow(next, source.column, table);
                    arr[idx] = next;
                    changed = true;
                }
                if (changed) await applyRows(arr);
            });
        },
        [allowUpdating, applyRows, bridge, readRows, runOp, selectedKeysRef, table],
    );

    // ----- Мультиудаление: только строки, доступные по построчному правилу -----
    const deleteSelectedRows = useCallback(() => {
        runOp('ошибка удаления выбранных строк', async () => {
            const keys = new Set(selectedKeysRef.current);
            const arr = readRows();
            const candidates = arr.filter((row) => keys.has(rowKeyOf(row)));
            const verdicts = await Promise.all(
                candidates.map((row) => bridge.evalRowRule(table.singleRowDeleteButtonRules, row).catch(() => true)),
            );
            const allowed = candidates.filter((_, idx) => verdicts[idx]);
            if (allowed.length === 0) return;
            const removed = new Set(allowed.map(rowKeyOf));
            const next = arr.filter((row) => !removed.has(rowKeyOf(row)));
            await applyRows(next);
            for (const row of allowed) {
                await bridge.onTableRowDeleted(next, row, table);
            }
            setSelectedKeys([]);
        });
    }, [applyRows, bridge, readRows, runOp, selectedKeysRef, table]);

    // ----- Добавление из справочника (addFormDataRows): открыт один набор за раз -----
    const [activeFormDataKey, setActiveFormDataKey] = useState<string | null>(null);
    const activeFormData = formDataSets.find((set) => set.key === activeFormDataKey)?.config;
    const openAddFormData = useCallback(
        (key: string) => {
            if (addCapabilitiesRef.current.pickable.includes(key)) setActiveFormDataKey(key);
        },
        [addCapabilitiesRef],
    );
    const closeAddFormData = useCallback(() => setActiveFormDataKey(null), []);
    const drawerAddExternalResolveRef = useRef<((rowKey: unknown) => void) | null>(null);
    const externalCodeKey = activeFormData?.setValues?.sets?.find((set) => set.attr === 'code')?.key;
    // Promise — контракт onValidate у DictpickerModal, сама проверка синхронная
    const validateExternalRows = useCallback(
        (data: IDictionaryData[]) => {
            const validators = activeFormData?.validators?.validators ?? [];
            const messages = validators
                .filter((validator) => !checkGridRowsByScript(data, validator.script))
                .map((validator) => validator.message);
            if (messages.length > 0) setErrorRef.current?.(messages);
            return Promise.resolve(messages.length === 0);
        },
        [setErrorRef, activeFormData],
    );
    const addExternalRows = useCallback(
        async (data: IDictionaryData[]) => {
            try {
                const config = activeFormData;
                if (!config) return;
                const arr = [...readRows()];
                let firstAddedRowKey: unknown;
                for (const selectItem of data) {
                    // дедуп по коду записи справочника
                    if (
                        externalCodeKey &&
                        arr.some(
                            (row) =>
                                String((row[externalCodeKey] as string | number | undefined) ?? '') ===
                                String(selectItem.code),
                        )
                    ) {
                        continue;
                    }
                    const item: TableRow = { '|NUM': uuidv4() };
                    await bridge.applyFormDataRow(config.setValues?.sets, table, item, selectItem);
                    await bridge.applyRowSets(table.newRowData?.setValues?.sets, table, item);
                    arr.push(item);
                    firstAddedRowKey ??= item['|NUM'];
                }
                if (firstAddedRowKey === undefined) return undefined;
                await applyRows(arr);
                return firstAddedRowKey;
            } catch (error) {
                console.error('EditTable: ошибка добавления строк из справочника', error);
                return undefined;
            }
        },
        [activeFormData, applyRows, bridge, externalCodeKey, readRows, table],
    );

    const settleDrawerAddExternal = useCallback((rowKey: unknown) => {
        const resolve = drawerAddExternalResolveRef.current;
        drawerAddExternalResolveRef.current = null;
        resolve?.(rowKey);
    }, []);
    const addFormDataRowsFromDrawer = useCallback(
        (key: string) =>
            new Promise<unknown>((resolve) => {
                if (!addCapabilitiesRef.current.pickable.includes(key)) {
                    resolve(undefined);
                    return;
                }
                ensureDrawerSessionSnapshot();
                // Одновременно может существовать только один выбор из справочника.
                drawerAddExternalResolveRef.current?.(undefined);
                drawerAddExternalResolveRef.current = resolve;
                setActiveFormDataKey(key);
            }),
        [addCapabilitiesRef, ensureDrawerSessionSnapshot],
    );
    useEffect(
        () => () => {
            drawerAddExternalResolveRef.current?.(undefined);
            drawerAddExternalResolveRef.current = null;
        },
        [],
    );

    // Операции панели не откатывают себя пооперационно: снимок всей формы на
    // каждое применение строки стоит столько же, сколько сама правка (на таблице
    // в тысячи строк — заметная пауза), а правка ячейки в гриде идёт тем же
    // пайплайном и тоже без отката. Сорвавшуюся операцию панель показывает
    // отказом и не пускает дальше, общий откат сеанса — «Отменить изменения».
    const onEditFormSubmit = useCallback(
        async (data: IField[], rowKey: unknown) => {
            const apply = async () => {
                if (!allowUpdating) return;
                ensureDrawerSessionSnapshot();
                const current = readRows();
                const rowIndex = current.findIndex((row) => row['|NUM'] === rowKey);
                if (rowIndex < 0) throw new Error('Строка больше не существует');
                const originalRow = current[rowIndex];
                const rowFieldKeys = table.customForm
                    ? undefined
                    : new Set(
                          [
                              ...Object.keys(originalRow),
                              ...collectTableColumns(table).map(({ column }) => column.key),
                              ...(table.tables ?? []).map((child) => child.key),
                          ].filter(
                              (key) =>
                                  !key.includes('|Document') &&
                                  !key.startsWith('#links|') &&
                                  !key.startsWith('|Parent|'),
                          ),
                      );
                const rowPatch: TableRow = {};
                applyEditFormFieldsToRowData(
                    data,
                    rowPatch,
                    fields,
                    bridge.setParentField,
                    rowFieldKeys,
                    table.customForm ? undefined : table.key,
                );
                // changes формы могли изменить состав таблицы: индекс до применения
                // документных полей больше не определяет редактируемую строку.
                const appliedRows = readRows();
                const appliedIndex = appliedRows.findIndex((row) => row['|NUM'] === rowKey);
                if (appliedIndex < 0) throw new Error('Строка больше не существует');
                const next: TableRow = { ...appliedRows[appliedIndex], ...rowPatch };
                formMethods.setValue(`${String(name)}.${appliedIndex}` as never, next as never, { shouldDirty: true });
                const updated = formMethods.getValues(name);
                // Секции автоформы уже исполнили каскады своих операций в её
                // черновике; применение родителя не повторяет события детей.
                await bridge.onTableChange(updated, table, !!table.customForm || !table.tables?.length);
                await bridge.onFormRowEdited(updated, next, table, !!table.customForm || !table.tables?.length);
                await onSubChangedRef.current?.();
                await revalidateTable();
                markDrawerChange();
            };
            return operations ? operations.run(apply) : apply();
        },
        // prettier-ignore
        [allowUpdating, bridge, ensureDrawerSessionSnapshot, fields, formMethods, markDrawerChange, name, readRows, revalidateTable, table, operations, onSubChangedRef],
    );

    const addRowFromDrawer = useCallback(async () => {
        ensureDrawerSessionSnapshot();
        const item = await createRow();
        pendingRevealRowRef.current = String(item['|NUM']);
        markDrawerChange();
        return item['|NUM'];
    }, [createRow, ensureDrawerSessionSnapshot, markDrawerChange]);

    const deleteRowFromDrawer = useCallback(
        async (rowKey: unknown) => {
            ensureDrawerSessionSnapshot();
            const current = readRows();
            const rowIndex = current.findIndex((row) => row['|NUM'] === rowKey);
            if (rowIndex < 0) throw new Error('Строка больше не существует');
            // Снимок под «вернуть строку»: удаление гонит ещё и onTableRowDeleted,
            // поэтому простой вставкой строки обратно его не отменить.
            const undoSnapshot = captureFormSnapshot();
            const remaining = await removeRow(current[rowIndex]);
            const nextRow = remaining[Math.min(rowIndex, remaining.length - 1)];
            if (undoSnapshot) setDrawerDeleteUndo({ snapshot: undoSnapshot, rowKey });
            setDrawerSessionChangeCount((count) => count + 1);
            return nextRow?.['|NUM'];
        },
        [captureFormSnapshot, ensureDrawerSessionSnapshot, readRows, removeRow],
    );

    const undoDrawerDelete = useCallback(async () => {
        if (!drawerDeleteUndo) return undefined;
        await restoreFormSnapshot(drawerDeleteUndo.snapshot);
        const rowKey = drawerDeleteUndo.rowKey;
        setDrawerDeleteUndo(null);
        setDrawerSessionChangeCount((count) => Math.max(0, count - 1));
        return rowKey;
    }, [drawerDeleteUndo, restoreFormSnapshot]);

    const onRowReorder = useCallback(
        ({
            rows: moved,
            targetRow,
            position,
        }: {
            rows: TableRow[];
            targetRow: TableRow;
            position: 'before' | 'after';
        }) => {
            runOp('ошибка перемещения строк', async () => {
                const current = readRows();
                const movedKeys = new Set(moved.map((m) => m['|NUM']));
                const rest = current.filter((r) => !movedKeys.has(r['|NUM']));
                const at = rest.findIndex((r) => r['|NUM'] === targetRow['|NUM']) + (position === 'after' ? 1 : 0);
                await applyRows([...rest.slice(0, at), ...moved, ...rest.slice(at)]);
            });
        },
        [applyRows, readRows, runOp],
    );

    // автофокус в ячейку после добавления (форма строки теперь единый drawer)
    const [editCellRequest, setEditCellRequest] = useState<{ rowId: string; field: string; token: number } | null>(
        null,
    );

    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

    // ----- Объединение строк (mergeRows) и тиражирование (splitRows) -----
    const [showMerge, setShowMerge] = useState(false);
    const [showSplit, setShowSplit] = useState(false);
    const onMergeApply = useCallback(
        (merged: TableRow[]) => {
            runOp('ошибка объединения строк', () => applyRows(merged));
        },
        [applyRows, runOp],
    );
    const onSplitApply = useCallback(
        (variants: TableRow[]) => {
            runOp('ошибка тиражирования строк', async () => {
                const selected = getSelectedRows();
                if (selected.length === 0) return;
                const initSplitRow = async (row: TableRow, variantIndex: number) => {
                    const sets = table.splitRows?.actions?.setValues?.sets;
                    const setsSelected = table.splitRows?.actions?.setValuesSelected?.sets;
                    // первая (variantIndex 0) строка получает setValuesSelected, остальные — setValues
                    const apply = variantIndex === 0 && setsSelected ? setsSelected : sets;
                    if (apply) await bridge.applyRowSets(apply, table, row);
                };
                const expanded = await buildSplitRows(selected, variants, initSplitRow);
                const byNum = new Map(expanded.map((e) => [e.sourceNum, e.rows]));
                // первая строка варианта встаёт на место исходной, остальные — следом
                const next = readRows().flatMap((row) => byNum.get(row['|NUM']) ?? [row]);
                await applyRows(next);
                setSelectedKeys([]);
            });
        },
        [applyRows, bridge, getSelectedRows, readRows, runOp, table],
    );

    // «Добавить строку» в меню есть только когда своё добавление разрешено правилом;
    // наборы из FormData — по пункту на каждый доступный
    const ownRowChoice = useMemo(
        () =>
            buttonRules.canAdd
                ? { value: 'row', label: table.addRowButtonName || 'Добавить строку', handler: addRow }
                : undefined,
        [addRow, buttonRules.canAdd, table.addRowButtonName],
    );
    const setChoices = useMemo(
        () =>
            pickableSets.map((set) => ({
                value: `formData:${set.key}`,
                label: set.config.name,
                handler: () => openAddFormData(set.key),
            })),
        [openAddFormData, pickableSets],
    );
    const addMenuItems = useMemo(() => tableAddMenuItems(ownRowChoice, setChoices), [ownRowChoice, setChoices]);
    // единственный способ добавления (своя строка или один набор) — сразу кнопка: меню из одного пункта — лишний щелчок
    const addChoices = ownRowChoice ? [ownRowChoice, ...setChoices] : setChoices;
    const soleChoice = addChoices.length === 1 ? addChoices[0] : undefined;
    const toolbarStart =
        allowUpdating && (buttonRules.canAdd || setChoices.length > 0 || table.mergeRows || table.splitRows) ? (
            <>
                {/* Два способа добавления (своя строка и выбор из справочника) —
                    это одна кнопка с меню, а не две кнопки подряд: паритет старого
                    тулбара, где меню собиралось из тех же пунктов. */}
                {soleChoice ? (
                    <button
                        type="button"
                        className="dtb__icon-btn dtb__icon-btn--label"
                        onClick={soleChoice.handler}
                        data-testid={soleChoice === ownRowChoice ? 'table-next-add-row' : 'table-next-add-form-data'}
                    >
                        {tableAddChoiceIcon(soleChoice.value)}
                        {soleChoice.label}
                    </button>
                ) : addChoices.length > 1 ? (
                    <Menu
                        list={addMenuItems}
                        position="bottom-start"
                        popoverProps={{ className: TABLE_ADD_MENU_CLASS, listItemAlign: 'start' }}
                    >
                        <TableToolbarButton
                            label={table.addRowButtonName || 'Добавить'}
                            icon={<MdAdd aria-hidden />}
                            showLabel
                            hasMenu
                            data-testid="table-next-add-menu"
                        />
                    </Menu>
                ) : null}
                {!!table.mergeRows && (
                    <button
                        type="button"
                        className="dtb__icon-btn dtb__icon-btn--label"
                        onClick={() => setShowMerge(true)}
                        data-testid="table-next-merge"
                    >
                        <MdMediation size="17" />
                        Объединение строк
                    </button>
                )}
                {!!table.splitRows && (
                    <button
                        type="button"
                        className="dtb__icon-btn dtb__icon-btn--label"
                        disabled={!hasSelection}
                        title={hasSelection ? undefined : 'Выберите строки для тиражирования'}
                        onClick={() => setShowSplit(true)}
                        data-testid="table-next-split"
                    >
                        <MdContentCopy size="17" />
                        Тиражирование
                    </button>
                )}
            </>
        ) : undefined;

    const showRowForm = table.showRowForm !== false;
    const showCopyActions = buttonRules.canCopy || !!table.copyRow;
    const activeKinds = useMemo(
        () => ({
            edit: allowUpdating && overview.edit,
            copy: showCopyActions && overview.copy,
            del: buttonRules.canRemove && overview.del,
        }),
        [overview, allowUpdating, showCopyActions, buttonRules.canRemove],
    );

    const getDeleteDrawerRowActionState = useCallback(
        (row: TableRow): TableFormDeleteActionState => {
            if (!buttonRules.canRemove) return { visible: false, enabled: false };
            const cache = rowButtonRulesRef.current;
            const enabled = (cache.get(row) ?? cache.last(row))?.del ?? true;
            return {
                visible: enabled || !!table.singleRowDeleteButtonDisabledTitle,
                enabled,
                disabledReason: enabled
                    ? undefined
                    : (table.singleRowDeleteButtonDisabledTitle ?? 'Удаление недоступно'),
            };
        },
        [buttonRules.canRemove, rowButtonRulesRef, table.singleRowDeleteButtonDisabledTitle],
    );
    // Корешок строки — красная полоса слева при проблемах в ячейках.
    // Считается тем же getCellState, что рисует ячейки, поэтому полоса
    // не может разойтись с тем, что видно внутри строки.
    const rowStatus = useCallback(
        (row: TableRow): 'error' | undefined => {
            if (viewMode) return undefined;
            for (const source of collectTableColumns(schema)) {
                if (columnVisibility.get(source.columnId) === false) continue;
                const rules = columnRulesRef.current.for(row, source.columnId);
                const col = source.column as ITableColumn;
                const readOnly = col.readonly === 'true' || rules?.readonly === true;
                if (getCellState(source.column, row, cellDeps, rules, readOnly) === 'invalid') return 'error';
            }
            return undefined;
        },
        [viewMode, schema, columnVisibility, columnRulesRef, cellDeps],
    );

    // контекст напрямую, а не useAnalytics(): хук бросает без провайдера, а
    // таблица рендерится и вне него (юниты, Storybook)
    const analytics = useContext(AnalyticsContext)?.analytics;
    const onExport = useCallback(() => {
        analytics?.trackEvent({
            category: 'DataTable',
            action: `Клик по кнопке экспорта таблицы данных '${table.name}'`,
            name: `document-table-export--${table.id}`,
            customDimensions: [{ id: 101, value: docId ?? '' }],
        });
        analytics?.deleteCustomDimensions([101]);
    }, [analytics, docId, table.id, table.name]);

    const rowActions = useCallback(
        (row: TableRow) => {
            if (!allowUpdating) {
                if (!showRowForm) return null;
                return (
                    <span className="tdn-action-slot">
                        <RowViewButton onOpen={() => openDrawer(row['|NUM'], 'view')} />
                    </span>
                );
            }
            // get ставит строку в очередь расчёта, last отдаёт прежнее решение,
            // пока он идёт: иначе правка ячейки на кадр показывает кнопки,
            // которые правило прячет, — они промаргивают у правленой строки
            const cache = rowButtonRulesRef.current;
            const rules = cache.get(row) ?? cache.last(row);
            const canCopyRow = activeKinds.copy && (rules?.copy ?? true);
            const canDeleteRow = activeKinds.del && (rules?.del ?? true);
            const canEditRow = activeKinds.edit && (rules?.edit ?? true);
            // слоты фиксированной ширины: скрытая per-row правилом кнопка держит
            // место — позиции соседних кнопок (и корзины в шапке) не съезжают
            return (
                <>
                    {showRowForm && (
                        <span className="tdn-action-slot">
                            {canEditRow ? (
                                <RowEditButton onOpen={() => openDrawer(row['|NUM'], 'edit')} />
                            ) : (
                                <RowViewButton onOpen={() => openDrawer(row['|NUM'], 'view')} />
                            )}
                        </span>
                    )}
                    {activeKinds.copy && (
                        <span className="tdn-action-slot">
                            {(canCopyRow || !!table.singleRowCopyButtonDisabledTitle) && (
                                <Tooltip
                                    openDelay={300}
                                    background="black"
                                    position="top"
                                    label={
                                        canCopyRow
                                            ? 'Копировать строку'
                                            : (table.singleRowCopyButtonDisabledTitle ?? 'Копирование недоступно')
                                    }
                                >
                                    <Button
                                        disabled={!canCopyRow}
                                        variant="text"
                                        tone="neutral"
                                        size="auto"
                                        aria-label="Копировать строку"
                                        onClick={() => copyRow(row)}
                                        startAdornment={<MdOutlineContentCopy size="18" />}
                                    />
                                </Tooltip>
                            )}
                        </span>
                    )}
                    {activeKinds.del && (
                        <span className="tdn-action-slot">
                            {(canDeleteRow || !!table.singleRowDeleteButtonDisabledTitle) && (
                                <Tooltip
                                    openDelay={300}
                                    background="black"
                                    position="top"
                                    label={
                                        canDeleteRow
                                            ? 'Удалить строку'
                                            : (table.singleRowDeleteButtonDisabledTitle ?? 'Удаление недоступно')
                                    }
                                >
                                    <Button
                                        disabled={!canDeleteRow}
                                        variant="text"
                                        tone="neutral"
                                        size="auto"
                                        aria-label="Удалить строку"
                                        onClick={() => deleteRow(row)}
                                        startAdornment={<MdOutlineDelete size="18" />}
                                    />
                                </Tooltip>
                            )}
                        </span>
                    )}
                </>
            );
        },
        // prettier-ignore
        [allowUpdating, showRowForm, activeKinds, copyRow, deleteRow, openDrawer, rowButtonRulesRef, table],
    );

    // ----- Master-detail: подтаблицы в раскрываемой строке -----
    const subTables = table.tables;
    const detailVisibility = useDetailVisibility(
        table,
        bridge,
        formMethods,
        fields,
        !displayOnly && !isDocumentRuntimeReadOnly(documentRuntime),
    );
    // Кэш меняет identity по мере досчёта правил (version) — это нужно обзору
    // строк ниже, а рисующие строку функции читают его через ref.
    const detailVisibilityRef = useLatest(detailVisibility);
    const visibleSubTablesOf = useCallback(
        (row: TableRow): IDocumentTable[] => {
            if (!subTables?.length) return [];
            const cache = detailVisibilityRef.current;
            const resolved = cache.get(row) ?? cache.last(row);
            // до resolve формул шеврон показываем (безопасный дефолт «видимо»)
            return resolved
                ? subTables.filter((st) => resolved.includes(st.key))
                : subTables.filter((st) => st.visibilityRules !== 'false');
        },
        [subTables, detailVisibilityRef],
    );
    const anyRowExpandable = !!subTables?.length && overview.expandable;
    // Непосчитанная строка читается как «кнопка видима»/«строка раскрывается» —
    // тот же безопасный дефолт, что и в самих кнопках: место держим, пока не
    // выяснится обратное. Пересчёт идёт и по version кэшей (правила считаются
    // асинхронно), иначе состав застыл бы на дефолтах первого кадра.
    const recomputeOverview = useCallback(() => {
        const visible = renderedRowsRef.current;
        if (visible.length === 0) {
            setOverview((prev) => (prev === OVERVIEW_DEFAULT ? prev : OVERVIEW_DEFAULT));
            return;
        }
        // Правила строк считаются по запросу, и обычно их просят сами кнопки —
        // но когда вида нет, просить некому: строка правится, а её правило так
        // и остаётся непосчитанным. Поэтому очередь наполняем здесь.
        rowButtonRules.request(visible);
        if (subTables?.length) detailVisibility.request(visible);
        // Состав меняем ТОЛЬКО по посчитанным правилам: непосчитанная строка
        // (правку ячейки обзор читает именно так) оставляет прежнее решение.
        // Иначе правка давала лишний кадр «показали по дефолту — скрыли по
        // расчёту», и кнопки со всей служебной зоной промаргивали.
        const anyTrue = (pick: (row: TableRow) => boolean | undefined, prev: boolean) => {
            let unknown = false;
            for (const row of visible) {
                const value = pick(row);
                if (value === undefined) unknown = true;
                else if (value) return true;
            }
            return unknown ? prev : false;
        };
        setOverview((prev) => {
            const next: IRowOverview = {
                edit: anyTrue((row) => rowButtonRules.peek(row)?.edit, prev.edit),
                copy: anyTrue((row) => rowButtonRules.peek(row)?.copy, prev.copy),
                del: anyTrue((row) => rowButtonRules.peek(row)?.del, prev.del),
                // Без подтаблиц признак не читается (anyRowExpandable), а его смена с дефолта
                // перерисовывала бы весь грид сразу после маунта — оставляем прежним.
                expandable: subTables?.length
                    ? anyTrue((row) => {
                          const resolved = detailVisibility.peek(row);
                          return resolved ? resolved.length > 0 : undefined;
                      }, prev.expandable)
                    : prev.expandable,
            };
            return prev.edit === next.edit &&
                prev.copy === next.copy &&
                prev.del === next.del &&
                prev.expandable === next.expandable
                ? prev
                : next;
        });
    }, [rowButtonRules, detailVisibility, subTables]);
    const onRenderedRowsChange = useCallback(
        (visible: TableRow[]) => {
            renderedRowsRef.current = visible;
            recomputeOverview();
        },
        [recomputeOverview],
    );
    useEffect(recomputeOverview, [recomputeOverview]);
    const canExpandRow = useCallback((row: TableRow) => visibleSubTablesOf(row).length > 0, [visibleSubTablesOf]);
    const renderDetail = useCallback(
        (row: TableRow) => {
            const list = formMethods.getValues(name);
            const arr: TableRow[] = Array.isArray(list) ? list : [];
            const idx = arr.findIndex((r) => r['|NUM'] === row['|NUM']);
            if (idx < 0) return null;
            return (
                <div className="tdn-detail">
                    {visibleSubTablesOf(row).map((subTable) => (
                        <EditTable
                            key={subTable.key}
                            table={subTable}
                            formMethods={formMethods}
                            fields={fields}
                            docId={docId}
                            isEdit={isEdit}
                            isNew={isNew}
                            setError={setError}
                            dataPath={`${String(name)}.${idx}.${subTable.key}`}
                            rowParent={row}
                            viewMode={viewMode}
                            displayOnly={displayOnly}
                            onSubChanged={async () => {
                                // изменение подтаблицы пересчитывает calc-колонки строки
                                // родителя (calculateRow по изменению подтаблицы)
                                const cur = formMethods.getValues(name);
                                const parentRows: TableRow[] = Array.isArray(cur) ? cur : [];
                                const parentIdx = parentRows.findIndex((r) => r['|NUM'] === row['|NUM']);
                                if (parentIdx < 0) return;
                                const parentNext = await bridge.calculateRow(
                                    { ...parentRows[parentIdx] },
                                    undefined,
                                    table,
                                );
                                formMethods.setValue(`${String(name)}.${parentIdx}` as never, parentNext as never, {
                                    shouldDirty: true,
                                });
                                await revalidateTable();
                            }}
                        />
                    ))}
                </div>
            );
        },
        // prettier-ignore
        [visibleSubTablesOf, formMethods, name, fields, docId, isEdit, isNew, setError, bridge, table, viewMode, displayOnly, revalidateTable],
    );

    // Версия вида строки для кэша строк грида: правила колонок, кнопок строки и подробностей досчитываются в
    // кэши по ключу строки — без новых колонок и нового объекта строки. Подробности ищут подтаблицу по позиции
    // строки в форме: удаление или перенос строк выше сдвигают её путь, поэтому позиция — тоже в версии.
    // Ячейке — только версия правил её колонки (`cellVersion`): досчёт правила не перерисовывает все ячейки строки.
    const rowIndex = useMemo(
        () => (anyRowExpandable ? new Map(rows.map((row, index) => [row, index])) : undefined),
        [anyRowExpandable, rows],
    );
    const rowVersion = (row: TableRow) =>
        `${columnRules.versionOf(row)}:${rowButtonRules.versionOf(row)}:${detailVisibility.versionOf(row)}:${rowIndex?.get(row) ?? ''}`;

    const fieldNavigation = useContext(DocumentFieldNavigationContext);
    useEffect(() => fieldNavigation?.register(String(name), async (target, isCurrent) => {
        const parent = target.parents?.[0];
        const rowId = parent?.rowId ?? target.rowId;
        const currentRows = readRows();
        const index = currentRows.findIndex((row) => rowKeyOf(row) === rowId);
        if (index < 0) return { message: 'Строка удалена или ещё не загружена.' };
        const row = currentRows[index];
        if (parent) {
            const childKey = target.parents?.[1]?.tableKey ?? target.tableKey;
            const child = visibleSubTablesOf(row).find((candidate) => candidate.key === childKey);
            if (!child) return { message: 'Вложенная таблица сейчас недоступна.' };
            if (!isCurrent()) return {};
            setRevealRowRequest({ rowId: rowId!, token: ++revealRequestTokenRef.current, focus: false, expand: true });
            return { nextPath: `${String(name)}.${index}.${child.key}` };
        }
        const source = collectTableColumns(schema).find((candidate) =>
            candidate.column.key === target.columnKey &&
            columnVisibility.get(candidate.columnId) !== false &&
            !('hidden' in candidate.column && candidate.column.hidden),
        );
        if (!source) return { message: 'Столбец скрыт правилами карточки или недоступен.' };
        // Паритет с editableOf/гридом: url и Boolean не редактируются в ячейке даже без
        // readonly-правила — иначе editCellRequest указывал бы на ячейку без редактора.
        const cellEditable =
            allowUpdating &&
            source.column.displayMode !== 'view' &&
            EDITABLE_KINDS.includes(source.kind) &&
            !('valueType' in source.column && source.column.valueType === ValueType.Boolean);
        const readonly = !cellEditable || (
            'readonly' in source.column && !!source.column.readonly &&
            !!await bridge.evalTableRuleValue(source.column.readonly, row)
        );
        if (!isCurrent()) return {};
        const token = ++revealRequestTokenRef.current;
        setRevealRowRequest({ rowId: rowId!, field: source.columnId, token, focus: readonly });
        if (!readonly) setEditCellRequest({ rowId: rowId!, field: source.columnId, token });
        else setEditCellRequest(null);
        return readonly ? { message: 'Поле показано. Изменение недоступно по правилам карточки.' } : {};
    }), [fieldNavigation, name, readRows, visibleSubTablesOf, schema, columnVisibility, allowUpdating, bridge]);

    if (!hasData) return null;

    const rowKinds = Number(showRowForm) + (allowUpdating ? Number(activeKinds.copy) + Number(activeKinds.del) : 0);
    // Массовое удаление предлагаем, только когда среди выбранного есть что
    // удалять: правило строки может запрещать удаление, и корзина над пачкой
    // неудаляемых строк — обещание, которого действие не выполнит.
    const showBulkDelete =
        allowUpdating &&
        buttonRules.canRemove &&
        hasSelection &&
        // без построчного правила удалять можно любую строку — спрашивать нечего
        (!table.singleRowDeleteButtonRules ||
            getSelectedRows().some((row) => (rowButtonRules.peek(row) ?? rowButtonRules.last(row))?.del ?? false));
    // Корзина живёт в шапке той же колонки и занимает слот: без него она не
    // влезает в трек и наезжает на заголовок соседней колонки.
    const actionKinds = Math.max(rowKinds, showBulkDelete ? 1 : 0);
    const selectable =
        (allowUpdating &&
            (buttonRules.canRemove || multiEditColumns.size > 0 || !!table.rowDragging || !!table.splitRows)) ||
        // «экспорт выбранных» без чекбоксов бессмыслен — в просмотре их включает он
        !!(table.allowExport && table.allowExportSelectedRows);
    // корзина массового удаления — только там, где строки вообще можно менять:
    // в просмотре чекбоксы включает экспорт выбранных, а удалять нечего
    const rowActionsHeader = showBulkDelete ? (
        <>
            {/* распорки-слоты — корзина встаёт ровно над корзиной строк.
                    Нужны, только когда у строк эта корзина есть: иначе слотов в
                    шапке станет больше, чем в строке, и она вылезет из трека */}
            {activeKinds.del && showRowForm && <span className="tdn-action-slot" />}
            {activeKinds.del && activeKinds.copy && <span className="tdn-action-slot" />}
            <span className="tdn-action-slot">
                <Tooltip openDelay={300} background="black" position="top" label="Удалить выбранные строки">
                    <Button
                        variant="text"
                        tone="neutral"
                        size="auto"
                        aria-label="Удалить выбранные строки"
                        onClick={() => setDeleteConfirmOpen(true)}
                        startAdornment={<MdOutlineDelete size="18" />}
                    />
                </Tooltip>
            </span>
        </>
    ) : undefined;

    const emptyState = dataKnown
        ? resolveTableEmptyState(table.emptyState, displayOnly, [
              ...(allowUpdating && buttonRules.canAdd
                  ? [{ id: 'addRow', label: table.addRowButtonName || 'Добавить строку', onClick: addRow }]
                  : []),
              ...pickableSets.map((set) => ({
                  id: formDataRowSetActionId(set),
                  label: set.config.name,
                  onClick: () => openAddFormData(set.key),
              })),
          ])
        : undefined;

    return (
        <>
            <TableData
                table={table}
                caption={
                    countInBlock ? undefined : table.captionFormula ? (
                        <ResolvedTableCaption
                            table={table}
                            fields={fields}
                            formMethods={formMethods}
                            isEdit={isEdit}
                            isNew={isNew}
                        />
                    ) : (
                        table.caption || (table.rowCount?.enabled ? 'Таблица' : undefined)
                    )
                }
                rows={rows}
                rowCount={
                    table.rowCount?.enabled && !countInBlock ? (
                        <TableRowCount count={totalRows} label={table.rowCount.label} />
                    ) : undefined
                }
                emptyPresentation={emptyState ? <TableEmptyState {...emptyState} /> : undefined}
                columns={columns}
                error={
                    table.emptyState?.enabled && linksFailed
                        ? 'Не удалось загрузить строки таблицы. Обновите документ, чтобы повторить загрузку.'
                        : undefined
                }
                loading={
                    linksPending ||
                    (formRows.length === 0 && allowUpdating && !!table.emptyState?.enabled && !buttonRules.ready)
                }
                onCellCommit={onCellCommit}
                editCellRequest={editCellRequest}
                revealRowRequest={revealRowRequest}
                // справка таблицы: на просмотре своя (viewTitle), а без неё — та же, что на редактировании
                help={(displayOnly ? table.viewTitle || table.title : table.title) || undefined}
                toolbarStart={
                    linksPending ? undefined : linksFailed ? (
                        table.emptyState?.enabled ? undefined : (
                            <LinksFailedNotice />
                        )
                    ) : (
                        toolbarStart
                    )
                }
                rowActions={actionKinds > 0 ? rowActions : undefined}
                rowActionsPosition="left"
                rowActionsAlwaysVisible
                rowActionsWidth={ROW_ACTION_SLOT * actionKinds || ROW_ACTION_SLOT}
                rowActionsHeader={rowActionsHeader}
                onRenderedRowsChange={onRenderedRowsChange}
                selectable={selectable}
                onSelectionChange={onSelectionChange}
                allowRowReordering={!!table.rowDragging && allowUpdating}
                onRowReorder={onRowReorder}
                rowStatus={rowStatus}
                rowVersion={rowVersion}
                cellVersion={columnRules.cellVersionOf}
                getHiddenIssues={getHiddenIssues}
                validationFailedEpoch={validationFailedEpoch}
                onExport={onExport}
                renderDetail={anyRowExpandable ? renderDetail : undefined}
                canExpandRow={anyRowExpandable ? canExpandRow : undefined}
                compact={!!dataPath}
                stickyHeader
                showColumnSets={table.allowColumnChooser !== false}
                viewMode={displayOnly}
            />
            {allowUpdating && deleteConfirmOpen && (
                <ConfirmPopup
                    title="Удаление строк"
                    text={`Удалить выбранные строки (${selectedKeys.length})? Будут удалены только строки, доступные для удаления.`}
                    okText="Удалить"
                    onConfirm={deleteSelectedRows}
                    onClose={() => setDeleteConfirmOpen(false)}
                />
            )}
            {allowUpdating && multiEditState && (
                <TableForm
                    table={buildMultiEditForm(table, multiEditState.source.column)}
                    docId={docId}
                    title="Значение для колонки"
                    isEdit
                    onClose={() => setMultiEditState(null)}
                    onSubmit={(data: IField[]) => {
                        const { source } = multiEditState;
                        setMultiEditState(null);
                        saveEditRowMulti(data, source);
                    }}
                    submitButtonCaption="Применить"
                    // одно поле для всех выбранных строк — это диалог, не карточка строки
                    shell="modal"
                    parentFieldMap={fields}
                    getParentFields={bridge.getParentFields}
                    rowIndex={0}
                    rowArray={[multiEditState.row]}
                    disablePagination
                    multiEdit
                />
            )}
            {drawerState && (
                <TableForm
                    table={table}
                    tableDataPath={String(name)}
                    parentAliases={parentAliases}
                    parentFieldMap={fields}
                    docId={docId}
                    title={table.name || 'Строка таблицы'}
                    isEdit={allowUpdating && drawerState.mode === 'edit'}
                    onClose={closeDrawer}
                    getParentFields={bridge.getParentFields}
                    onSubmit={allowUpdating && drawerState.mode === 'edit' ? onEditFormSubmit : undefined}
                    onCancelSession={cancelDrawerSession}
                    sessionChangeCount={drawerSessionChangeCount}
                    onAddRow={
                        allowUpdating && drawerState.mode === 'edit' && buttonRules.canAdd
                            ? addRowFromDrawer
                            : undefined
                    }
                    addRowButtonName={table.addRowButtonName}
                    onAddFormDataRows={
                        allowUpdating && drawerState.mode === 'edit' && setChoices.length > 0
                            ? addFormDataRowsFromDrawer
                            : undefined
                    }
                    addFormDataRowsOptions={formDataOptions}
                    onDeleteRow={allowUpdating && drawerState.mode === 'edit' ? deleteRowFromDrawer : undefined}
                    onUndoDelete={allowUpdating && drawerDeleteUndo ? undoDrawerDelete : undefined}
                    canAddRow={allowUpdating && drawerState.mode === 'edit' && buttonRules.canAdd}
                    getDeleteRowActionState={getDeleteDrawerRowActionState}
                    rowIndex={Math.max(
                        0,
                        rows.findIndex((row) => row['|NUM'] === drawerState.rowKey),
                    )}
                    rowArray={rows}
                >
                    {(row) =>
                        drawerState.mode === 'view' && table.signCheck ? (
                            <SignCheckContainer signCheck={table.signCheck} rowData={row} />
                        ) : null
                    }
                </TableForm>
            )}
            {allowUpdating && showMerge && table.mergeRows && (
                <MergeRowsModal
                    table={table}
                    settings={table.mergeRows}
                    rows={rows}
                    onApply={onMergeApply}
                    onClose={() => setShowMerge(false)}
                />
            )}
            {allowUpdating && showSplit && table.splitRows && (
                <SplitRowsModal
                    table={table}
                    settings={table.splitRows}
                    onApply={onSplitApply}
                    onClose={() => setShowSplit(false)}
                />
            )}
            {allowUpdating && activeFormData && (
                <DictpickerModal
                    key={activeFormDataKey}
                    docId={docId}
                    dictName={activeFormData.name}
                    modalTitle={activeFormData.name}
                    isFormData
                    isMultiple
                    predicatesCache=""
                    loadMode="all"
                    selectableLevels=""
                    visibleLevels=""
                    useClientSideDataProcessing={activeFormData.useClientSideDataProcessing}
                    hideSelected={activeFormData.hideSelected}
                    selected={
                        externalCodeKey
                            ? rows
                                  .map((row) => String((row[externalCodeKey] as string | number | undefined) ?? ''))
                                  .filter(Boolean)
                            : []
                    }
                    getFormValuesAsync={() => bridge.getFormValuesAsync(activeFormData.formValues, {})}
                    getFiltersAsync={() => bridge.getFiltersAsync(activeFormData.filters, {})}
                    gridAttribute={activeFormData.gridAttribute}
                    onSubmit={async (data) => {
                        setActiveFormDataKey(null);
                        const rowKey = await addExternalRows(data);
                        const addedFromDrawer = !!drawerAddExternalResolveRef.current;
                        if (rowKey !== undefined) {
                            if (addedFromDrawer) {
                                pendingRevealRowRef.current = String(rowKey);
                                markDrawerChange();
                            } else {
                                requestRowReveal(rowKey);
                            }
                        }
                        settleDrawerAddExternal(rowKey);
                    }}
                    onClose={() => {
                        closeAddFormData();
                        settleDrawerAddExternal(undefined);
                    }}
                    onValidate={validateExternalRows}
                    loadedDataValidators={activeFormData.loadedDataValidators}
                />
            )}
        </>
    );
};

// memo — обязательная пара к useDeferredValue в Table: срочный рендер со СТАРЫМ отложенным
// режимом обязан переиспользовать готовое поддерево (bailout по неизменным пропсам), иначе
// тяжёлая таблица рендерится дважды — сначала со старой схемой, потом с новой (замер на
// заявке с 1500 позициями: +1,3–2,7 с к полному времени перехода). Пропсы стабильны:
// table/fields/formMethods/setError держат identity между режимами (см. скип пересборки
// карты полей в useDocumentFields).
export default memo(EditTable) as typeof EditTable;
